import { IonSpinner } from "@ionic/react";
import Tooltip from "@mui/material/Tooltip";
import React from "react";
import { ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { isDisplayableEvaluatedPrice } from "../../../tools/ItineraryPricingEvaluationDisplay.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import ItineraryPriceBadge from "../pricing/ItineraryPriceBadge";
import {
    buildItineraryPricePillChromeLoadingStyle,
    ITINERARY_PRICE_PILL_CHROME_BASE_CLASSNAME,
    ITINERARY_PRICE_PILL_CHROME_FILLED_CLASSNAME,
} from "../pricing/itineraryPricePill.styles";
import PriceConversion from "../pricing/PriceConversion";
import { resolveItineraryDetailsIdleItineraryPrice } from "./itineraryDetails.tools";
import { type ItineraryDetailsOfferLinkDisplay } from "./itineraryDetailsOfferLink.types";

/**
 * Explains the trailing asterisk when at least one segment has no priced offer (`providerOffers === null`).
 */
const INCOMPLETE_OFFER_TOTAL_TOOLTIP = "Une partie des billets de l'itinéraire ne sont pas disponibles à l'achat direct et ne sont pas inclus dans le prix.";

/**
 * Props for the offer/price badge rendered in the itinerary panel chrome row.
 */
interface ItineraryDetailsOfferPricePillProps {
    "itinerary":               ItineraryDTO;
    "isEuro":                  boolean;
    "isDarkUi":                boolean;
    "offerStatus"?:            "idle" | "loading" | "success" | "error";
    "computedOfferTotalPrice": number | null;

    /**
     * When true, at least one pricing segment has `providerOffers === null`; the total may omit that segment.
     */
    "showIncompleteOfferTotalFootnote"?: boolean;

    /** Used to mirror GA pricing adjustments on the itinerary price badge. */
    "pricingPreferences": ItineraryPricingPreferencesDTO;

    /** Rental itinerary fallback link when the offer total is missing but segment URLs exist. */
    "rentalItineraryOfferLink"?: ItineraryDetailsOfferLinkDisplay | null;

    /**
     * `default` — bordered pill (legacy).
     * `chrome` — filled stamp badge for the redesigned itinerary-details header.
     */
    "variant"?: "default" | "chrome";
}

/**
 * Renders the offer loading/success/error states or the idle itinerary price badge for the panel chrome.
 */
const ItineraryDetailsOfferPricePill: React.FC<ItineraryDetailsOfferPricePillProps> = ( {
    itinerary,
    isEuro,
    isDarkUi,
    offerStatus = "idle",
    computedOfferTotalPrice,
    showIncompleteOfferTotalFootnote = false,
    pricingPreferences,
    rentalItineraryOfferLink = null,
    variant = "default",
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const offerLoadingAccent = palette.itineraryDetailsOfferLoadingAccent;
    const isChromeVariant = variant === "chrome";
    const chromeBaseClassName = isChromeVariant
        ? ITINERARY_PRICE_PILL_CHROME_BASE_CLASSNAME
        : "inline-flex shrink-0 items-center justify-center rounded-lg border-2 border-solid px-1 text-xs font-bold";
    const chromeFilledClassName = isChromeVariant
        ? ITINERARY_PRICE_PILL_CHROME_FILLED_CLASSNAME
        : `${ chromeBaseClassName } border-green-price text-green-price`;
    const chromePaddingStyle: React.CSSProperties = isChromeVariant
        ? {}
        : { "padding": "4px 6px 3px 6px" };
    const showLoadingOffer = offerStatus === "loading";
    const showSuccessOffer = offerStatus === "success";
    const showErrorOffer = offerStatus === "error";
    const showIncompleteOfferTotalTooltip = showIncompleteOfferTotalFootnote === true;
    const rentalOfferLinkOpensExternal = rentalItineraryOfferLink != null;
    const idleItineraryDisplayPrice = resolveItineraryDetailsIdleItineraryPrice( itinerary, pricingPreferences );
    const tempFakeChromePriceChf = ITINERARY_PANEL.tempFakeChromePriceChf;
    const hasOfferSuccessBadgeContent = isDisplayableEvaluatedPrice( computedOfferTotalPrice ) ||
      rentalItineraryOfferLink != null;

    const renderNumericPriceBadge = ( price: number ): React.ReactElement => (
        <span
            className={chromeFilledClassName}
            style={chromePaddingStyle}
        >
            <PriceConversion
                chooseEuro={isEuro}
                className={isChromeVariant ? "text-inherit" : "text-green-price"}
                compact={true}
                price={price}
                showFromPrefix={false}
            />
        </span>
    );

    if ( showLoadingOffer ) {
        return (
            <span
                className={chromeBaseClassName}
                style={isChromeVariant
                    ? {
                        ...chromePaddingStyle,
                        ...buildItineraryPricePillChromeLoadingStyle( offerLoadingAccent ),
                    }
                    : {
                        ...chromePaddingStyle,
                        "borderColor": offerLoadingAccent,
                        "color":       offerLoadingAccent,
                    }}
            >
                <IonSpinner
                    className="mr-1 h-3 w-3 shrink-0"
                    style={{ "color": offerLoadingAccent }}
                />
                Calcul de l&apos;offre...
            </span>
        );
    }

    if ( showSuccessOffer && hasOfferSuccessBadgeContent ) {
        return (
            <Tooltip
                arrow
                disableFocusListener={showIncompleteOfferTotalTooltip === false}
                disableHoverListener={showIncompleteOfferTotalTooltip === false}
                disableTouchListener={showIncompleteOfferTotalTooltip === false}
                placement="top"
                slotProps={{ "popper": { "sx": { "zIndex": 2147483647 } } }}
                title={showIncompleteOfferTotalTooltip ? INCOMPLETE_OFFER_TOTAL_TOOLTIP : undefined}
            >
                <span
                    className={`${ chromeBaseClassName } ${
                        isChromeVariant
                            ? rentalOfferLinkOpensExternal
                                ? "cursor-pointer bg-green-price text-white shadow-green-price/30 hover:bg-green-price/90"
                                : showIncompleteOfferTotalTooltip
                                    ? "cursor-help bg-green-price/15 text-green-price shadow-green-price/15"
                                    : "bg-green-price text-white shadow-green-price/30"
                            : rentalOfferLinkOpensExternal
                                ? "cursor-pointer border-green-price text-green-price hover:border-green-price/80 hover:bg-green-price/10"
                                : showIncompleteOfferTotalTooltip
                                    ? "cursor-help border-green-price text-green-price"
                                    : "border-green-price text-green-price"
                    }`}
                    onClick={rentalOfferLinkOpensExternal
                        ? () => {
                            window.open( rentalItineraryOfferLink.bookingUrl, "_blank", "noopener,noreferrer" );
                        }
                        : undefined}
                    onKeyDown={rentalOfferLinkOpensExternal
                        ? ( keyboardEvent ) => {
                            if ( keyboardEvent.key === "Enter" || keyboardEvent.key === " " ) {
                                keyboardEvent.preventDefault();
                                window.open( rentalItineraryOfferLink.bookingUrl, "_blank", "noopener,noreferrer" );
                            }
                        }
                        : undefined}
                    role={rentalOfferLinkOpensExternal ? "link" : undefined}
                    style={chromePaddingStyle}
                    tabIndex={rentalOfferLinkOpensExternal ? 0 : undefined}
                >
                    {isDisplayableEvaluatedPrice( computedOfferTotalPrice )
                        ? (
                            <span className="inline-flex items-baseline gap-0">
                                <PriceConversion
                                    chooseEuro={isEuro}
                                    className={isChromeVariant ? "text-inherit" : "text-green-price"}
                                    compact={true}
                                    price={computedOfferTotalPrice}
                                    showFromPrefix={false}
                                />
                                {showIncompleteOfferTotalFootnote
                                    ? (
                                        <span
                                            aria-hidden={true}
                                            className={isChromeVariant ? "text-inherit" : "text-green-price"}
                                        >
                                            *
                                        </span>
                                    )
                                    : null}
                            </span>
                        )
                        : rentalItineraryOfferLink != null
                            ? (
                                <span className="inline-flex items-center gap-1">
                                    <span>
                                        {rentalItineraryOfferLink.label}
                                    </span>
                                    <span
                                        aria-hidden={true}
                                        className="text-[9px] font-bold opacity-70"
                                    >
                                        ↗
                                    </span>
                                </span>
                            )
                            : null}
                </span>
            </Tooltip>
        );
    }

    if ( showErrorOffer ) {
        if ( tempFakeChromePriceChf != null ) {
            return renderNumericPriceBadge( tempFakeChromePriceChf );
        }

        return (
            <span
                className={chromeBaseClassName}
                style={{
                    ...chromePaddingStyle,
                    "borderColor": isChromeVariant ? undefined : "#dc2626",
                    "color":       isChromeVariant ? "#ffffff" : "#dc2626",
                    "background":  isChromeVariant ? "#dc2626" : undefined,
                    "boxShadow":   isChromeVariant ? "0 2px 10px rgba(220, 38, 38, 0.28)" : undefined,
                }}
            >
                Offre indisponible
            </span>
        );
    }

    if ( offerStatus === "idle" && idleItineraryDisplayPrice !== null ) {
        return (
            <span
                className={chromeFilledClassName}
                style={chromePaddingStyle}
            >
                <ItineraryPriceBadge
                    className={isChromeVariant ? "text-inherit" : "text-green-price"}
                    isEuro={isEuro}
                    itinerary={itinerary}
                    pricingPreferences={pricingPreferences}
                />
            </span>
        );
    }

    if ( tempFakeChromePriceChf != null ) {
        return renderNumericPriceBadge( tempFakeChromePriceChf );
    }

    return null;
};

export default ItineraryDetailsOfferPricePill;
